// thrux-cart-cross-sell.js — Pass 2 D1 (deviation #46).
//
// Handles "+ Add" clicks on the server-rendered cross-sell line in
// snippets/thrux-cross-sell-slot.liquid.
//
// Cart math is owned by three native `discountAutomaticBasic` records
// (Discounts A / B / C at 2+ / 5+ / 10+ cans). This script does NOT touch
// pricing — it only manipulates cart line items. When the customer clicks
// "+ Add", the appropriate native discount fires automatically because the
// cart quantity crosses a tier threshold.
//
// Three click paths (mirrors the three triggers in the snippet):
//
//   variety   (cart has 1 can):
//     → Adds the other TWO flavors at qty 1 each. JS reads which flavor
//       is in cart from the snippet's data attrs and sends the missing
//       two via `/cart/add.js` items[] batch.
//     → Cart auto-resolves to 3 × $9.99 = $29.97 via Discount A.
//
//   fill_5    (cart has 3 or 4 cans):
//     → Adds enough cans to reach 5 total. Distributes to lowest-quantity
//       flavors first (algorithm below). Existing flavor preference is
//       respected — never introduces a new flavor.
//     → Cart auto-resolves to 5 × $5.99 = $29.95 via Discount B.
//
//   fill_10   (cart has 5-9 cans):
//     → Adds enough cans to reach 10 total. Same lowest-fill-first
//       distribution.
//     → Cart auto-resolves to 10 × $8.99 = $89.90 via Discount C.
//
// After mutation, refetch /cart.js and dispatch `cart:update` so Horizon's
// cart drawer auto-rerenders. The snippet sees the new cart shape +
// `_thrux_xsell_accepted` already set, so it suppresses (one-shot rule).
//
// Two attribute writes happen per click:
//   • Line-item property `_cross_sell_trigger: 'true'` on each new line —
//     KEPT for attribution analytics on which upsell path a customer took.
//   • Cart attribute `_thrux_xsell_accepted: 'true'` — drives the one-shot
//     suppression in the snippet. Replaces the legacy
//     `_thrux_xsell_savings` numeric flag (no Function reads it anymore).

(function () {
  'use strict';

  function init() {
    document.addEventListener('click', onDocumentClick);
  }

  function onDocumentClick(e) {
    const cta = e.target.closest('[data-thrux-cross-sell-add]');
    if (!cta) return;
    e.preventDefault();
    e.stopPropagation();

    const root = cta.closest('[data-thrux-cross-sell]');
    if (!root) return;
    if (cta.disabled) return;

    cta.disabled = true;
    cta.classList.add('is-loading');

    const kind = root.dataset.xsellKind;

    let plan;
    try {
      plan = buildPlan(kind, root);
    } catch (err) {
      console.warn('[thrux-cross-sell] plan failed:', err);
      cta.disabled = false;
      cta.classList.remove('is-loading');
      return;
    }

    if (!plan || plan.items.length === 0) {
      cta.disabled = false;
      cta.classList.remove('is-loading');
      return;
    }

    addItemsAndMarkAccepted(plan.items)
      .then(refreshCartAndDispatch)
      .catch(err => console.warn('[thrux-cross-sell] add failed:', err))
      .finally(() => {
        cta.disabled = false;
        cta.classList.remove('is-loading');
      });
  }

  // --- Plan builder ---------------------------------------------------------

  function buildPlan(kind, root) {
    const ciderId = root.dataset.ciderId;
    const redlineId = root.dataset.redlineId;
    const neonId = root.dataset.neonId;
    const cider = parseInt(root.dataset.ciderQty || '0', 10);
    const redline = parseInt(root.dataset.redlineQty || '0', 10);
    const neon = parseInt(root.dataset.neonQty || '0', 10);

    if (kind === 'variety') {
      // Add the two flavors NOT in cart, qty 1 each.
      const items = [];
      if (cider === 0) items.push({ id: ciderId, qty: 1 });
      if (redline === 0) items.push({ id: redlineId, qty: 1 });
      if (neon === 0) items.push({ id: neonId, qty: 1 });
      return { items };
    }

    if (kind === 'fill_5' || kind === 'fill_10' || kind === 'founders_3') {
      // founders_3 fires from the 2-can branch when the founders tier is
      // visible; same distribution algorithm as fill_5/fill_10 (lowest
      // existing flavor count fills first) so the picked flavor pattern
      // reads natural to the customer. After the add, cart total is 3 —
      // founders Function then emits its $4.98 order discount to land
      // the cart at $24.99 (strict floor; beats any 15% code at qty=3).
      const target = kind === 'fill_5' ? 5 : (kind === 'fill_10' ? 10 : 3);
      const additions = distributeToLowestFirst(
        target - (cider + redline + neon),
        [
          { id: ciderId, qty: cider },
          { id: redlineId, qty: redline },
          { id: neonId, qty: neon },
        ]
      );
      const items = additions
        .filter(a => a.add > 0)
        .map(a => ({ id: a.id, qty: a.add }));
      return { items };
    }

    return null;
  }

  // Returns [{id, qty (current), add (number to add)}, ...]. Skips flavors
  // not currently in cart (respect existing flavor preference). If only ONE
  // flavor is in cart, all additions go to that flavor.
  function distributeToLowestFirst(needed, flavors) {
    const inCart = flavors.filter(f => f.qty > 0).map(f => ({ ...f, add: 0 }));
    if (inCart.length === 0) return [];
    for (let i = 0; i < needed; i++) {
      let pick = inCart[0];
      for (const f of inCart) {
        if ((f.qty + f.add) < (pick.qty + pick.add)) pick = f;
      }
      pick.add += 1;
    }
    return inCart;
  }

  // --- Network -------------------------------------------------------------

  function addItemsAndMarkAccepted(items) {
    const payload = {
      items: items.map(it => ({
        id: parseInt(it.id, 10),
        quantity: it.qty,
        properties: { _cross_sell_trigger: 'true' },
      })),
    };
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(payload),
    })
      .then(r => {
        if (!r.ok) throw new Error('cart/add ' + r.status);
        return r.json();
      })
      .then(() => {
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ attributes: { _thrux_xsell_accepted: 'true' } }),
        });
      })
      .then(r => {
        if (!r.ok) throw new Error('cart/update ' + r.status);
        return r.json();
      });
  }

  function refreshCartAndDispatch() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(r => r.json())
      .then(cart => {
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: 'thrux-cross-sell',
            data: { source: 'cross-sell', itemCount: cart.item_count },
          },
        }));
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
